import React, { useState, useEffect } from "react";
import {
  MantineProvider,
  Container,
  Title,
  LoadingOverlay,
  Alert,
  Accordion,
  Group, 
  Paper,
  Text
} from "@mantine/core";
import { Footer } from "../../components/Footer/Footer";
import { djangoAxiosInstance } from "../../api";

import TenantInfoSection from "./components/TenantInfoSection";
import SubscriptionSection from "./components/SubscriptionSection";
import UsersSection from "./components/UsersSection";
import classes from './TenantAdmin.module.css';
import TenantSettingsSection from "./components/TenantSettingsSection";

const theme = {};

function AccordionLabel({ label, image, description }) {
  return (
    <Group wrap="nowrap">
      <div>
        <Text>{label}</Text>
        <Text size="sm" c="dimmed" fw={400}>
          {description}
        </Text>
      </div>
    </Group>
  );
}

const formatDate = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const TenantAdminDashboardPage = () => {
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("access_token");
        const headers = {};
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }
        const response = await djangoAxiosInstance.get("/tenants/", {
          headers,
        });
        setTenantData(response.data);
      } catch (err) {
        console.error("Error fetching tenant data:", err);
        setError("Failed to load tenant data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, []);

  const handleUpdateUsers = (subscriptionId, updatedUsers) => {
    if (tenantData && tenantData.subscriptions) {
      const updatedSubscriptions = tenantData.subscriptions.map((subscription) => {
        if (subscription.id === subscriptionId) {
          return {
            ...subscription,
            subscription_seats: updatedUsers,
          };
        }
        return subscription;
      });

      setTenantData({
        ...tenantData,
        subscriptions: updatedSubscriptions,
      });
    }
  };

  const sortedSubscriptions = tenantData?.subscriptions ? 
    [...tenantData.subscriptions].sort((a, b) => {
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      
      const dateA = new Date(a.start_date || a.created_at || 0);
      const dateB = new Date(b.start_date || b.created_at || 0);
      return dateB - dateA;
    }) : [];

  const defaultAccordionValue = sortedSubscriptions.length > 0 ? sortedSubscriptions[0].id : null;

  return (
    <MantineProvider theme={theme}>
      <Container size="lg" py="xl" pos="relative">
        <LoadingOverlay visible={loading} />

        <Title order={1} mb="sm" mt="lg">
          Admin Dashboard
        </Title>

        {error ? (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        ) : (
          <>
            <TenantInfoSection tenant={tenantData} />
            <TenantSettingsSection tenant={tenantData} />
            {tenantData && tenantData.subscriptions && tenantData.subscriptions.length > 0 ? (
            
            <Paper>
              <Title order={1} mb="sm" mt="lg">
                Subscriptions
              </Title>
              <Accordion defaultValue={defaultAccordionValue} classNames={classes}>
                {sortedSubscriptions.map((subscription) => (
                  <Accordion.Item key={subscription.id} value={subscription.id}>
                    <Accordion.Control>
                      <AccordionLabel {
                        ...{
                          label: `${subscription.plan_type.toUpperCase()} - ${subscription.status}`, 
                          image: "", 
                          description: `${formatDate(subscription.start_date)} - ${formatDate(subscription.end_date)}`
                        }
                        }/>
                      
                    </Accordion.Control>
                    <Accordion.Panel>
                      <SubscriptionSection subscription={subscription} />
                      <UsersSection
                        subscription={subscription}
                        onUpdateUsers={(updatedUsers) => handleUpdateUsers(subscription.id, updatedUsers)}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
              </Paper>
            ) : (
              <Alert color="blue" title="No Subscriptions">
                No active subscriptions found for this tenant.
              </Alert>
            )}
          </>
        )}
      </Container>
      <Footer />
    </MantineProvider>
  );
};

export default TenantAdminDashboardPage;